import React from 'react';
import { useParams } from 'react-router-dom';
import { useQueryClient, useMutation } from '@tanstack/react-query';
import { Box } from '@mui/material';
import { useConfirm } from 'material-ui-confirm';
import {useJourneyStep} from 'api/Journeys/journey_queries';
import { useAuth } from 'contexts/AuthContext';

import {apiV1OrgUri} from 'constants/apiRoutes';
import {Button, Typography, Tooltip} from '@mui/material';
import {CompareArrowsTwoTone} from '@mui/icons-material';
import {flowStateChip} from '../../JourneyConstants';
import EditTemporalAttribute from 'components/EditTemporalAttribute';
import BranchFilterEdit from 'pages/Journeys/components/Sidebar/BranchFilter/BranchFilterEdit';
import BranchFilterDraft from 'pages/Journeys/components/Sidebar/BranchFilter/BranchFilterDraft';
import BranchFilterActive from 'pages/Journeys/components/Sidebar/BranchFilter/BranchFilterActive';
import BranchFilterHistory from 'pages/Journeys/components/Sidebar/BranchFilter/BranchFilterHistory';
import BranchFilterReport from './BranchFilter/BranchFilterReport';



const BranchFilterSidebar = ({stepId, hideSidebar}) => {
    const { id: journeyIdStr } = useParams();
    const journeyId = parseInt(journeyIdStr);
    const queryClient = useQueryClient();
    const confirm = useConfirm();
    const { axiosElementalApiCurrentOrg } = useAuth();

    const { data: journeyStep, isFetching } = useJourneyStep(journeyId, stepId);

    const statusBadge = flowStateChip(journeyStep?.status, journeyStep?.has_changes, 'small');

    const mutationBaseUrl = `${apiV1OrgUri}/flows/${journeyId}/steps/branch_filter/${stepId}/filter/draft`;

    // mutations for temporal_attributes specific to this step
    const createDraftMutation = useMutation(
        async () => {
            return await axiosElementalApiCurrentOrg.post(`${mutationBaseUrl}`);
        }, {
            onSuccess: (data) => {
                queryClient.invalidateQueries(['journeys', journeyId]);
                // queryClient.invalidateQueries(['journeys', journeyId, 'adjacencyList', 'draft']);
            }
        });

    const deleteDraftMutation = useMutation(
        async () => {
            return await axiosElementalApiCurrentOrg.delete(`${mutationBaseUrl}`);
        }, {
            onSuccess: (data) => {
                queryClient.invalidateQueries(['journeys', journeyId]);
                queryClient.invalidateQueries(['journey_step', stepId]);
                // queryClient.invalidateQueries(['journeys', journeyId, 'adjacencyList', 'draft']);
            }
        });

    const activateDraftMutation = useMutation(
        async () => {
            return await axiosElementalApiCurrentOrg.post(`${mutationBaseUrl}/activate`);
        }, {
            onSuccess: (data) => {
                queryClient.invalidateQueries(['journeys', journeyId]);
                queryClient.invalidateQueries(['journey_step', stepId]);
                // queryClient.invalidateQueries(['journeys', journeyId, 'adjacencyList', 'draft']);
            }
        });

    // Flip Split mutation
    const mutationFlipSplit = useMutation({
        mutationFn: async () => {
            return await axiosElementalApiCurrentOrg.put(`/flows/${journeyId}/${stepId}/swap`);
        },
        onSuccess: () => {
            queryClient.invalidateQueries(['journeys', journeyId]);
            // queryClient.invalidateQueries(['journeys', journeyId, 'adjacencyList', 'draft']);
        }
    });

    const handleFlipSplit = () => {
        confirm({
            title: 'Flip Branches',
            description: 'Are you sure you want to flip the branch conditions? This will switch the "Match" and "No Match" branches.',
            confirmationText: 'Flip',
            cancellationText: 'Cancel',
            dialogProps: {
                sx: {
                    zIndex: 1450,
                    '& .MuiDialog-paper': {
                        zIndex: (theme) => 1450 + 1,
                    }
                },
            }
        }).then(() => {
            mutationFlipSplit.mutate();
        }).catch(() => {
            // User cancelled, do nothing
        });
    };

    if(isFetching) {
        return <Box>Loading ...</Box>;
    }

    return (
        <Box className='flow-sidebar__body' p={0}>
            <Box className='flow-sidebar__body-content' display='flex' flexDirection='column'>
                <Box display='flex' justifyContent='space-between' style={{width: '100%', alignItems: 'center'}}>
                    <Typography variant='subtitle1' style={{width: '100%', display: 'inline-flex', alignItems: 'center'}}>Branch On Conditions<span style={{marginLeft: 10}}>{statusBadge}</span></Typography>
                    <Box display='inline-flex' alignItems='center'>
                        <Tooltip placement='top' title='Flip Branches'>
                            <Button 
                                size='small' 
                                variant='outlined'
                                className='flow-sidebar__actions' 
                                onClick={handleFlipSplit}
                                startIcon={<CompareArrowsTwoTone fontSize='inherit'/>}
                                sx={{ whiteSpace: 'nowrap' }}
                            >
                                Swap Branches
                            </Button>
                        </Tooltip>
                    </Box>
                </Box>
                <EditTemporalAttribute
                    attributeTypeLabel={'Conditions'}

                    active={journeyStep.filter}
                    draft={journeyStep.filter_draft}
                    history={journeyStep.filter_history}
                    journeyStep={journeyStep}

                    ComponentEditDraft={BranchFilterEdit}
                    componentEditDraftProps={{
                        journeyId: journeyId,
                        journeyStepId: journeyStep.id
                    }}
                    ComponentDisplayDraft={BranchFilterDraft}
                    ComponentDisplayActive={BranchFilterActive}
                    ComponentDisplayHistory={BranchFilterHistory}

                    parentAllowsActivation={ journeyStep.status === 'active'}

                    mutationCreateDraft={createDraftMutation}
                    mutationDeleteDraft={deleteDraftMutation}
                    mutationActivateDraft={activateDraftMutation}

                />
                {journeyStep.status === 'active' && <BranchFilterReport stepId={stepId} />}
            </Box>
        </Box>
    );
};


export default BranchFilterSidebar;
